import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatoFecha } from '../../../lib/formularios';

/*
 * Campos de texto libre: respuesta corta, párrafo, fecha y hora.
 * Todos reciben { item, valor, onCambio, soloLectura, idCampo, aria } donde `aria` trae
 * aria-invalid / aria-describedby / aria-labelledby ya resueltos por la tarjeta.
 */

export function SinRespuesta() {
  return <span className="fr-sin-respuesta">Sin respuesta</span>;
}

/** Tipo de teclado según la validación: un número abre el teclado numérico en el teléfono. */
function atributosDeValidacion(item) {
  const v = item.validacion;
  if (v?.tipo === 'numero') return { inputMode: 'decimal', type: 'text' };
  if (v?.tipo === 'texto' && v.op === 'correo') return { inputMode: 'email', type: 'email', autoComplete: 'email' };
  if (v?.tipo === 'texto' && v.op === 'url') return { inputMode: 'url', type: 'url' };
  return { type: 'text' };
}

export function CampoTextoCorto({ item, valor, onCambio, soloLectura, idCampo, aria }) {
  if (soloLectura) return <div className="fr-valor-leido">{valor ? String(valor) : <SinRespuesta />}</div>;
  return (
    <input
      id={idCampo}
      className="fm-campo-linea fr-texto-corto"
      placeholder="Tu respuesta"
      value={valor ?? ''}
      maxLength={1000}
      onChange={(e) => onCambio(e.target.value === '' ? null : e.target.value)}
      {...atributosDeValidacion(item)}
      {...aria}
    />
  );
}

export function CampoParrafo({ valor, onCambio, soloLectura, idCampo, aria }) {
  const ref = useRef(null);
  // Crece con el texto, como en Google: un párrafo largo no debe esconderse detrás de un scroll interno
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [valor]);
  if (soloLectura) return <div className="fr-valor-leido fr-valor-parrafo">{valor ? String(valor) : <SinRespuesta />}</div>;
  return (
    <textarea
      ref={ref}
      id={idCampo}
      rows={1}
      className="fm-campo-linea fr-parrafo"
      placeholder="Tu respuesta"
      value={valor ?? ''}
      maxLength={10000}
      onChange={(e) => onCambio(e.target.value === '' ? null : e.target.value)}
      {...aria}
    />
  );
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const dos = (n) => String(n).padStart(2, '0');

/** Separa "2026-09-16T14:30" / "--09-16" en sus partes. */
function partesDeFecha(valor) {
  const m = /^(\d{4}|--)-(\d{2})-(\d{2})(?:T(\d{2}:\d{2}))?$/.exec(String(valor || ''));
  if (!m) return { anio: '', mes: '', dia: '', hora: '' };
  return { anio: m[1] === '--' ? '' : m[1], mes: m[2], dia: m[3], hora: m[4] || '' };
}

export function CampoFecha(props) {
  if (props.soloLectura) return <div className="fr-valor-leido">{props.valor ? formatoFecha(props.valor) : <SinRespuesta />}</div>;
  return <CampoFechaEditable {...props} />;
}

function CampoFechaEditable({ item, valor, onCambio, idCampo, aria }) {
  const conAnio = item.fecha?.incluir_anio !== false;
  const conHora = !!item.fecha?.incluir_hora;
  // Las partes viven aquí y no solo en `valor`: elegir el mes antes que el día todavía no es una
  // fecha (el valor sigue vacío), y sin estado propio el mes elegido se perdería al instante.
  const [p, setP] = useState(() => partesDeFecha(valor));
  const emitido = useRef(valor ?? null);
  useEffect(() => {
    // Solo se reinicia si el valor cambió por fuera (borrar formulario, precompletar, borrador)
    if ((valor ?? null) !== emitido.current) {
      emitido.current = valor ?? null;
      setP(partesDeFecha(valor));
    }
  }, [valor]);

  // Sin fecha completa no hay valor: una hora suelta no dice nada en una pregunta de fecha
  const emitir = (cambio) => {
    const n = { ...p, ...cambio };
    setP(n);
    let nuevo = null;
    if (n.mes && n.dia && (!conAnio || n.anio)) {
      const base = `${conAnio ? n.anio : '--'}-${dos(n.mes)}-${dos(n.dia)}`;
      nuevo = conHora && n.hora ? `${base}T${n.hora}` : base;
    }
    emitido.current = nuevo;
    onCambio(nuevo);
  };

  return (
    <div className="fr-fecha">
      {conAnio ? (
        <label className="fr-subcampo">
          <span className="fr-subetiqueta">Fecha</span>
          <input
            id={idCampo}
            type="date"
            className="fm-campo-linea"
            value={p.anio && p.mes && p.dia ? `${p.anio}-${p.mes}-${p.dia}` : ''}
            onChange={(e) => {
              const [anio, mes, dia] = (e.target.value || '').split('-');
              emitir({ anio: anio || '', mes: mes || '', dia: dia || '' });
            }}
            {...aria}
          />
        </label>
      ) : (
        <>
          <label className="fr-subcampo">
            <span className="fr-subetiqueta">Mes</span>
            <select
              id={idCampo}
              className="fm-campo-linea fr-select-linea"
              value={p.mes ? String(Number(p.mes)) : ''}
              onChange={(e) => emitir({ mes: e.target.value ? dos(e.target.value) : '' })}
              {...aria}
            >
              <option value="">MM</option>
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="fr-subcampo fr-subcampo-corto">
            <span className="fr-subetiqueta">Día</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={31}
              placeholder="DD"
              className="fm-campo-linea"
              value={p.dia ? String(Number(p.dia)) : ''}
              onChange={(e) => {
                const d = parseInt(e.target.value, 10);
                emitir({ dia: d >= 1 && d <= 31 ? dos(d) : '' });
              }}
              aria-invalid={aria['aria-invalid']}
            />
          </label>
        </>
      )}
      {conHora && (
        <label className="fr-subcampo">
          <span className="fr-subetiqueta">Hora</span>
          <input
            type="time"
            className="fm-campo-linea"
            value={p.hora}
            onChange={(e) => emitir({ hora: e.target.value || '' })}
            aria-invalid={aria['aria-invalid']}
          />
        </label>
      )}
    </div>
  );
}

export function CampoHora({ item, valor, onCambio, soloLectura, idCampo, aria }) {
  const duracion = !!item.hora?.duracion;
  if (soloLectura) return <div className="fr-valor-leido">{valor ? String(valor) : <SinRespuesta />}</div>;

  if (!duracion) {
    return (
      <input
        id={idCampo}
        type="time"
        className="fm-campo-linea fr-hora"
        value={valor || ''}
        onChange={(e) => onCambio(e.target.value || null)}
        {...aria}
      />
    );
  }

  const [h = '', m = '', s = ''] = String(valor || '').split(':');
  const emitir = (cambio) => {
    const partes = { h, m, s, ...cambio };
    if (partes.h === '' && partes.m === '' && partes.s === '') {
      onCambio(null);
      return;
    }
    onCambio(`${dos(partes.h || 0)}:${dos(partes.m || 0)}:${dos(partes.s || 0)}`);
  };
  const campo = (etiqueta, clave, actual, max, extra = {}) => (
    <label className="fr-subcampo fr-subcampo-corto">
      <span className="fr-subetiqueta">{etiqueta}</span>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={max}
        className="fm-campo-linea"
        value={actual === '' ? '' : String(Number(actual))}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          emitir({ [clave]: Number.isNaN(n) ? '' : String(Math.min(Math.max(n, 0), max)) });
        }}
        aria-invalid={aria['aria-invalid']}
        {...extra}
      />
    </label>
  );
  return (
    <div className="fr-fecha">
      {campo('Horas', 'h', h, 99, { id: idCampo, 'aria-describedby': aria['aria-describedby'] })}
      <span className="fr-dos-puntos" aria-hidden="true">:</span>
      {campo('Minutos', 'm', m, 59)}
      <span className="fr-dos-puntos" aria-hidden="true">:</span>
      {campo('Segundos', 's', s, 59)}
    </div>
  );
}
